import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { clientsApi } from '@/services/clients'

export type OverdueBlockingMode = 'warning' | 'hard' | null

// 2.7.12 / 2.11.13 (BR06) - effective blocking mode for a client, driven by
// Settings › Document numbering (overdue_blocking toggle + mode):
//  - null    → blocking off or no unpaid invoice → surface nothing
//  - warning → Create job shows an inline notice under the client field
//  - hard    → Create job blocks on Save with a toast (see JobFormPage.submit)
export function useOverdueBlockingMode(
  clientId: string | undefined
): OverdueBlockingMode {
  const { data } = useQuery({
    queryKey: ['clients', clientId, 'overdue-blocking'],
    queryFn: () => clientsApi.overdueBlocking(clientId as string),
    enabled: !!clientId,
  })
  return data?.data?.mode ?? null
}

interface Props {
  clientId: string | undefined
  mode?: OverdueBlockingMode
}

// Inline warning surface for Create job (2.3.3). Renders only in "Warning only"
// mode; "Strict blocking" is enforced when Save is clicked in JobFormPage.
export function OverduePaymentWarning({ clientId, mode: modeProp }: Props) {
  const { t: tPayments } = useTranslation('payments')
  const { t: tJobs } = useTranslation('jobs')
  const queriedMode = useOverdueBlockingMode(clientId)
  const mode = modeProp ?? queriedMode

  if (mode === 'warning') {
    return (
      <div className='flex items-start gap-2 rounded-md bg-warning/10 px-3 py-2 text-sm text-foreground'>
        <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-warning' />
        <p>{tPayments('overdueWarning.inlineMessage')}</p>
      </div>
    )
  }

  if (mode === 'hard') {
    return (
      <div className='flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'>
        <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-destructive' />
        <p>{tJobs('form.overdueBlocked')}</p>
      </div>
    )
  }

  return null
}

export default OverduePaymentWarning
